/**
 * Work → Calendar (/work/calendar). What needs doing and when: tasks (and
 * the next repeats of every routine), SyncSuite's live calls, events, goal
 * dates and affiliate moves, a week or a month at a time.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CalendarPlus, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button, Skeleton, useToast } from "@/components/ui";
import { cn } from "@/lib/utils";
import { Card, Empty, PageHeader, Segmented } from "@/features/command/ui";
import { TaskDrawer, type TaskTarget } from "@/features/work/TaskDrawer";
import { PersonSwitch, personId, storePerson, storedPerson } from "@/features/work/PersonSwitch";
import { WeekGrid, sortDay } from "@/features/work/calendar/WeekGrid";
import { MonthGrid } from "@/features/work/calendar/MonthGrid";
import { CalChip, Legend } from "@/features/work/calendar/CalChip";
import { EventModal, type EventTarget } from "@/features/work/calendar/EventModal";
import {
  addDays, addMonths, findTask, fmtDay, fromYmd, hhmmIn, joinDue, localHHmm, projectRepeats, relDay, todayYmd, useLookups, useMe, weekStart, zonedInstant,
} from "@/features/work/lib";
import type { CalItem, CalendarData, WorkTask } from "@/features/work/types";

type Mode = "week" | "month";

export function WorkCalendarPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { people, wsTz, loaded } = useLookups();
  const { me, ready } = useMe();
  const mode: Mode = params.get("view") === "month" ? "month" : "week";
  const today = todayYmd();
  const anchor = /^\d{4}-\d{2}-\d{2}$/.test(params.get("day") ?? "") ? params.get("day")! : today;
  const person = params.get("person") || storedPerson();
  const assignee = personId(person, me, people);

  const [data, setData] = useState<CalendarData | null>(null);
  const [open, setOpen] = useState<WorkTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState<TaskTarget | null>(null);
  const [eventTarget, setEventTarget] = useState<EventTarget | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  const range = useMemo(() => {
    if (mode === "week") {
      const from = weekStart(anchor);
      return { from, to: addDays(from, 6), days: Array.from({ length: 7 }, (_, i) => addDays(from, i)), month: anchor.slice(0, 7) };
    }
    const first = `${anchor.slice(0, 7)}-01`;
    const from = weekStart(first);
    const days = Array.from({ length: 42 }, (_, i) => addDays(from, i));
    return { from, to: days[41]!, days, month: anchor.slice(0, 7) };
  }, [mode, anchor]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cal, tasks] = await Promise.all([
        api.get<CalendarData>("/command/calendar", { from: range.from, to: range.to, assignee }),
        api.get<{ tasks: WorkTask[] }>("/tasks", { status: "open", limit: 500, assignee_id: assignee }).catch(() => ({ tasks: [] as WorkTask[] })),
      ]);
      setData(cal);
      setOpen(tasks.tasks);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't load the calendar");
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, assignee, toast]);

  useEffect(() => { if (ready && loaded) void load(); }, [ready, loaded, load]);

  // The server's items, with quick-added "no time" tasks (noon in the
  // workspace's timezone) shown at the top of the day, plus each routine's
  // next repeats drawn faintly.
  const items = useMemo<CalItem[]>(() => {
    if (!data) return [];
    const fixed = data.items.map((it) => {
      if (it.type !== "task" || !it.start) return it;
      const noon = zonedInstant(it.date, "12:00", wsTz);
      return it.start === localHHmm(noon) || it.start === hhmmIn(noon, data.tz) ? { ...it, start: null } : it;
    });
    const ghosts: CalItem[] = [];
    for (const t of open) {
      if (!t.recurrence) continue;
      for (const d of projectRepeats(t, range.from, range.to)) {
        if (fixed.some((x) => x.type === "task" && x.date === d && x.title === t.title)) continue;
        ghosts.push({
          id: `ghost:${t.id}:${d}`, type: "task", title: t.title, date: d, start: null, minutes: null, ghost: true, task_id: t.id,
          color: t.source_color, source_name: t.source_name, repeat: true, ai: !!t.agent_slug, owner: t.assignee_name,
        });
      }
    }
    return [...fixed, ...ghosts];
  }, [data, open, range.from, range.to, wsTz]);

  const setParam = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) { if (v) next.set(k, v); else next.delete(k); }
    setParams(next, { replace: true });
  };
  const shift = (dir: -1 | 1) => setParam({ day: mode === "week" ? addDays(anchor, 7 * dir) : addMonths(anchor, dir) });
  const pickPerson = (v: string) => { storePerson(v); setParam({ person: v }); };

  const openItem = async (it: CalItem) => {
    if (it.type === "task") {
      const id = it.task_id ?? it.id;
      const hit = open.find((t) => t.id === id);
      if (hit) { setDrawer(hit); return; }
      const found = await findTask(id);
      if (found) setDrawer(found);
      else toast.error("Couldn't open that task");
      return;
    }
    if (it.type === "event") { setEventTarget({ item: it }); return; }
    if (it.type === "goal") { navigate(`/work/goals?goal=${it.id}`); return; }
    if (it.type === "live_call" && it.link) window.open(it.link, "_blank", "noopener,noreferrer");
    if (it.type === "appointment" && it.contact_id) navigate(`/contacts/${it.contact_id}`);
  };

  const addTaskOn = (day: string) => setDrawer({ isNew: true, defaults: { due_at: joinDue(day, "", wsTz), assignee_id: assignee ?? null } });

  const label = mode === "week"
    ? `${fmtDay(range.from, { month: "short", day: "numeric" })} to ${fmtDay(range.to, { month: range.from.slice(5, 7) === range.to.slice(5, 7) ? undefined : "short", day: "numeric" })}, ${range.to.slice(0, 4)}`
    : fromYmd(`${range.month}-15`).toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const inThisRange = today >= range.from && today <= range.to && (mode === "week" || anchor.slice(0, 7) === today.slice(0, 7));
  const selectedDay = mode === "month" ? selected ?? (anchor.slice(0, 7) === today.slice(0, 7) ? today : null) : null;
  const sel = selectedDay ? sortDay(items.filter((i) => i.date === selectedDay)) : null;

  return (
    <div className="space-y-4 pb-12">
      <PageHeader
        eyebrow="Work"
        title="Calendar"
        subtitle="What needs doing and when: tasks and routines, live calls, events, goal dates and affiliate moves."
        actions={
          <>
            <Button variant="outline" leftIcon={<CalendarPlus className="h-4 w-4" />} onClick={() => setEventTarget({ day: selectedDay ?? (inThisRange ? today : range.from) })}>Add event</Button>
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => addTaskOn(selectedDay ?? (inThisRange ? today : range.from))}>New task</Button>
          </>
        }
      />

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-xl border border-zinc-800 bg-zinc-900/60 p-0.5">
            <button type="button" onClick={() => shift(-1)} className="rounded-[10px] p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100" aria-label="Back"><ChevronLeft className="h-4 w-4" /></button>
            <button type="button" onClick={() => setParam({ day: null })} className={cn("rounded-[10px] px-2.5 text-xs font-medium", inThisRange ? "text-zinc-500" : "text-zinc-200 hover:bg-zinc-800")}>Today</button>
            <button type="button" onClick={() => shift(1)} className="rounded-[10px] p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100" aria-label="Next"><ChevronRight className="h-4 w-4" /></button>
          </div>
          <h2 className="text-base font-semibold text-zinc-100">{label}</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
          <Segmented<Mode> size="sm" value={mode} onChange={(v) => { setSelected(null); setParam({ view: v === "month" ? "month" : null }); }} options={[{ value: "week", label: "Week" }, { value: "month", label: "Month" }]} />
          <div className="-mx-1 max-w-full overflow-x-auto px-1">
            <PersonSwitch value={person} onChange={pickPerson} people={people} me={me} size="sm" />
          </div>
        </div>
      </div>

      <Legend />

      {!data ? (
        mode === "week" ? (
          <div className="grid grid-cols-1 gap-2 lg:grid-cols-7">{Array.from({ length: 7 }).map((_, i) => <Skeleton key={i} className="h-24 lg:h-[460px]" />)}</div>
        ) : <Skeleton className="h-[560px] w-full" />
      ) : (
        <div className={cn("transition-opacity", loading && "opacity-60")}>
          {mode === "week" ? (
            <WeekGrid days={range.days} items={items} today={today} onOpen={(it) => void openItem(it)} onAdd={addTaskOn} showOwner={!assignee} />
          ) : (
            <div className="space-y-3">
              <MonthGrid days={range.days} month={range.month} items={items} today={today} selected={selectedDay} onSelect={setSelected} onOpen={(it) => void openItem(it)} />
              {selectedDay && sel && (
                <Card className="p-3">
                  <div className="mb-2 flex items-center gap-2 px-1">
                    <span className="text-sm font-semibold text-zinc-100">{relDay(selectedDay, today)}</span>
                    {relDay(selectedDay, today).length < 10 && <span className="text-xs text-zinc-500">{fmtDay(selectedDay)}</span>}
                    <button type="button" onClick={() => setParam({ view: null, day: selectedDay })} className="ml-auto text-xs text-indigo-300 hover:underline">See the week</button>
                    <button type="button" onClick={() => addTaskOn(selectedDay)} className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-100"><Plus className="h-3 w-3" />Add</button>
                  </div>
                  {sel.untimed.length + sel.timed.length === 0 ? (
                    <Empty title="Nothing planned for this day" />
                  ) : (
                    <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
                      {[...sel.untimed, ...sel.timed].map((it) => <CalChip key={it.id} it={it} onOpen={(x) => void openItem(x)} showOwner={!assignee} />)}
                    </div>
                  )}
                </Card>
              )}
            </div>
          )}
        </div>
      )}

      <TaskDrawer target={drawer} onClose={() => setDrawer(null)} onChanged={load} />
      <EventModal target={eventTarget} onClose={() => setEventTarget(null)} onSaved={load} people={people} meId={me?.id ?? null} />
    </div>
  );
}
